import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const PARTICLE_COUNT = 80;
const CONNECTION_DISTANCE = 1.8;
const SPEED = 0.004;
const MAX_PAIRS = (PARTICLE_COUNT * (PARTICLE_COUNT - 1)) / 2;

function ParticleConstellation({ mouseRef, darkMode }) {
  const dotColor    = darkMode ? "#888880" : "#909088";
  const lineColor   = darkMode ? "#666660" : "#b0b0a8";
  const dotOpacity  = darkMode ? 0.65 : 0.35;
  const lineOpacity = darkMode ? 0.35 : 0.18;

  const prevMouseRef = useRef({ x: 0, y: 0 });

  const particles = useMemo(() =>
    Array.from({ length: PARTICLE_COUNT }, () => {
      const bvx = (Math.random() - 0.5) * SPEED;
      const bvy = (Math.random() - 0.5) * SPEED;
      return {
        x: (Math.random() - 0.5) * 12,
        y: (Math.random() - 0.5) * 7,
        z: 0,
        vx: bvx, vy: bvy,
        bvx, bvy, // base velocity to return to
      };
    })
  , []);

  const pointsGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(PARTICLE_COUNT * 3);
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return geo;
  }, []);

  const linesGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(MAX_PAIRS * 6);
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return geo;
  }, []);

  useFrame(() => {
    const mouse = mouseRef.current;
    const prev  = prevMouseRef.current;

    // Mouse velocity in world space
    const mvx = mouse ? (mouse.x - prev.x) * 6 : 0;
    const mvy = mouse ? (mouse.y - prev.y) * 4 : 0;

    // Update previous mouse
    if (mouse) {
      prev.x = mouse.x;
      prev.y = mouse.y;
    }

    const mouseSpeed = Math.sqrt(mvx * mvx + mvy * mvy);
    const mx = mouse ? mouse.x * 6 : 9999;
    const my = mouse ? mouse.y * 4 : 9999;

    particles.forEach((p) => {
      // Gently drift back to base velocity
      p.vx += (p.bvx - p.vx) * 0.006;
      p.vy += (p.bvy - p.vy) * 0.006;

      p.x += p.vx;
      p.y += p.vy;

      if (Math.abs(p.x) > 6.5) { p.vx *= -1; p.bvx *= -1; }
      if (Math.abs(p.y) > 4)   { p.vy *= -1; p.bvy *= -1; }

      // Subtle inertia nudge — only when mouse is moving
      if (mouseSpeed > 0.001) {
        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 1.5 && dist > 0) {
          const strength = (1 - dist / 1.5) * mouseSpeed * 0.018;
          p.vx += (mvx / mouseSpeed) * strength;
          p.vy += (mvy / mouseSpeed) * strength;
        }
      }
    });

    // Update dot positions
    const pp = pointsGeo.attributes.position.array;
    particles.forEach((p, i) => {
      pp[i * 3]     = p.x;
      pp[i * 3 + 1] = p.y;
      pp[i * 3 + 2] = p.z;
    });
    pointsGeo.attributes.position.needsUpdate = true;

    // Build connecting lines
    const lp = linesGeo.attributes.position.array;
    let idx = 0;
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x;
        const dy = particles[i].y - particles[j].y;
        if (dx * dx + dy * dy < CONNECTION_DISTANCE * CONNECTION_DISTANCE) {
          lp[idx++] = particles[i].x;
          lp[idx++] = particles[i].y;
          lp[idx++] = 0;
          lp[idx++] = particles[j].x;
          lp[idx++] = particles[j].y;
          lp[idx++] = 0;
        }
      }
    }
    linesGeo.setDrawRange(0, idx / 3);
    linesGeo.attributes.position.needsUpdate = true;
  });

  return (
    <>
      <points geometry={pointsGeo}>
        <pointsMaterial
          color={dotColor}
          size={0.06}
          transparent
          opacity={dotOpacity}
          sizeAttenuation
        />
      </points>
      <lineSegments geometry={linesGeo}>
        <lineBasicMaterial color={lineColor} transparent opacity={lineOpacity} />
      </lineSegments>
    </>
  );
}

export default ParticleConstellation;
